import { useMutation } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useRole } from '@/hooks/useRole';
import { checkNetworkStatus } from '@/hooks/useNetworkStatus';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { useRegisterStockMovement } from '@/hooks/useStock';
import { supabase } from '@/integrations/supabase/client';
import {
  buildQuickSaleFinanceEntry,
  buildQuickSaleOrderPayload,
  buildQuickSaleStockLines,
  type QuickSaleCartItem,
} from '@/lib/quickSale';
import { todayIsoDate } from '@/lib/financeStatus';
import type { Client, PaymentMethod } from '@/types/database';

export interface FinalizeQuickSaleInput {
  cart: QuickSaleCartItem[];
  client: Client | null;
  paymentMethod: PaymentMethod;
}

export type QuickSaleFinanceStatus =
  | 'created'
  | 'queued'
  | 'skipped_permission'
  | 'failed';

export interface QuickSaleResult {
  quoteId: string;
  offline: boolean;
  financeStatus: QuickSaleFinanceStatus;
  financeEntryId: string | null;
  stockRequested: number;
  stockApplied: number;
  stockFailed: string[];
}

function hasOfflineId(value: string | null | undefined): boolean {
  return Boolean(value && String(value).startsWith('offline_'));
}

export function useQuickSale() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const { canSeeFinance, canDelete } = useRole();
  const {
    saveQuoteOffline,
    saveFinanceEntryOffline,
    saveStockMovementOffline,
  } = useOfflineSync();
  const registerStockMovement = useRegisterStockMovement();

  return useMutation({
    // A Venda Rápida possui ramo offline real. Sem offlineFirst o TanStack
    // pausaria antes de entrar no mutationFn quando a conexão cai com a tela aberta.
    networkMode: 'offlineFirst' as const,
    mutationFn: async (input: FinalizeQuickSaleInput): Promise<QuickSaleResult> => {
      if (!user?.id) throw new Error('Faça login para finalizar a venda.');
      if (!activeCompany?.id) throw new Error('Selecione uma empresa para finalizar a venda.');
      if (input.cart.length === 0) throw new Error('O carrinho está vazio.');

      const order = buildQuickSaleOrderPayload({
        userId: user.id,
        companyId: activeCompany.id,
        cart: input.cart,
        client: input.client,
      });

      const online = await checkNetworkStatus();
      // Uma dependência offline (cliente/produto ainda não sincronizado) obriga
      // toda a cadeia a passar pela fila, mesmo que a rede já tenha voltado. A
      // fila resolve os UUIDs reais antes de estoque e financeiro.
      const queueWholeSale =
        !online ||
        hasOfflineId(activeCompany.id) ||
        hasOfflineId(input.client?.id) ||
        input.cart.some((item) => hasOfflineId(item.product_id));

      let quoteId: string;
      if (queueWholeSale) {
        quoteId = saveQuoteOffline(
          {
            quote: order.quote,
            items: order.items,
            userId: user.id,
            companyId: activeCompany.id,
            // REGRA DE PRODUTO: Venda Rápida é grátis e fora de qualquer cota.
            // A flag precisa ir na operação offline; não é um esquecimento.
            countTowardQuoteQuota: order.countTowardQuoteQuota,
          },
          { silent: true },
        );
      } else {
        const { user_id, ...quoteFields } = order.quote;
        const { data: quote, error: quoteError } = await supabase
          .from('quotes')
          .insert({ ...quoteFields, user_id })
          .select('id')
          .single();
        if (quoteError || !quote) {
          throw new Error(quoteError?.message || 'Não foi possível criar o pedido da venda.');
        }
        quoteId = quote.id;

        const items = order.items.map((item) => ({ ...item, quote_id: quoteId }));
        const { error: itemsError } = await supabase.from('quote_items').insert(items);
        if (itemsError) {
          // `member` pode atualizar, mas não excluir quotes. Marcar cancelado
          // impede que uma venda sem itens pareça concluída quando a segunda
          // etapa falha; owner/admin ainda recebe a limpeza completa.
          await supabase
            .from('quotes')
            .update({
              status: 'cancelled',
              notes: 'Venda rápida incompleta: falha ao gravar itens.',
            })
            .eq('id', quoteId);
          if (canDelete) await supabase.from('quotes').delete().eq('id', quoteId);
          throw new Error(`Não foi possível gravar os itens da venda: ${itemsError.message}`);
        }
      }

      window.dispatchEvent(new CustomEvent('quotes-updated'));

      const stockLines = buildQuickSaleStockLines(input.cart);
      const stockFailed: string[] = [];
      let stockApplied = 0;

      if (queueWholeSale) {
        for (const line of stockLines) {
          saveStockMovementOffline({
            movement: {
              product_id: line.productId,
              delta: -line.quantity,
              reason: 'quote_sale',
              quote_id: quoteId,
              service_order_id: null,
              note: 'Venda rápida',
            },
            userId: user.id,
            companyId: activeCompany.id,
          });
          stockApplied += 1;
        }
      } else if (stockLines.length > 0) {
        // Guard anti-dupla-baixa do mesmo caminho usado pelo prompt normal.
        // Se o guard não puder ser lido, não arriscamos duplicar movimento.
        const { data: existing, error: guardError } = await supabase
          .from('stock_movements')
          .select('product_id')
          .eq('company_id', activeCompany.id)
          .eq('quote_id', quoteId)
          .eq('reason', 'quote_sale');

        if (guardError) {
          stockFailed.push(...stockLines.map((line) => line.name));
        } else {
          const alreadyMoved = new Set((existing ?? []).map((row) => String(row.product_id)));
          for (const line of stockLines) {
            if (alreadyMoved.has(line.productId)) {
              stockApplied += 1;
              continue;
            }
            try {
              await registerStockMovement.mutateAsync({
                productId: line.productId,
                delta: -line.quantity,
                reason: 'quote_sale',
                quoteId,
                note: 'Venda rápida',
              });
              stockApplied += 1;
            } catch {
              stockFailed.push(line.name);
            }
          }
        }
      }

      const financeEntry = buildQuickSaleFinanceEntry({
        userId: user.id,
        companyId: activeCompany.id,
        quoteId,
        clientId: input.client?.id ?? null,
        amount: order.quote.total,
        paymentMethod: input.paymentMethod,
        paidDate: todayIsoDate(),
      });

      let financeStatus: QuickSaleFinanceStatus = 'skipped_permission';
      let financeEntryId: string | null = null;

      // Financeiro é owner/admin-only. Para `member`, a venda continua
      // concluída e o resultado explica que o lançamento não foi criado.
      if (canSeeFinance) {
        // A rede pode cair depois de o pedido ter sido criado. Nesse caso o
        // lançamento entra na fila com o quote_id real em vez de se perder.
        const financeOnline = queueWholeSale ? false : await checkNetworkStatus();
        if (!financeOnline) {
          financeEntryId = saveFinanceEntryOffline({
            entry: financeEntry,
            userId: user.id,
          });
          financeStatus = 'queued';
        } else {
          const { data: created, error: financeError } = await supabase
            .from('finance_entries')
            .insert(financeEntry)
            .select('id')
            .single();
          if (financeError || !created) {
            financeStatus = 'failed';
          } else {
            financeStatus = 'created';
            financeEntryId = created.id;
            window.dispatchEvent(new CustomEvent('finance-updated'));
          }
        }
      }

      return {
        quoteId,
        offline: queueWholeSale,
        financeStatus,
        financeEntryId,
        stockRequested: stockLines.length,
        stockApplied,
        stockFailed,
      };
    },
  });
}
